/**
 * DI-2-2 — `groupSet(rows)`: the whole pre-grouping of one drop, derived.
 *
 * Pure and idempotent: same rows → deep-equal output; the input is never
 * mutated. Runs on the main thread over the store (< 10 ms for 1 000 rows —
 * no worker): once after phase 1 (clusters, flights, cadence) and again when
 * the hash phase is complete (`withDuplicates: true`, duplicates decided).
 *
 * Every row index lands in EXACTLY ONE of: a cluster's ground group, a
 * provisional flight, a cluster's no-time group, the dropped duplicates, or
 * `noPosition` (nothing discarded silently, DI-D10).
 *
 * DI-3-S74 §3.1 — a known-camera row without a position joins the flight it
 * was taken in (`joinFlights.ts`): it lands in that flight (or the cluster's
 * ground group) and in the cluster's `joined`, never in `noPosition`; the
 * ones no flight takes are `unassigned` (inside `noPosition`).
 */
import { type ClusterPoint } from "./cluster.js";
import { groupByFlightAverages } from "./flightGroups.js";
import { findDuplicates, findSameNameDifferentPayload } from "./duplicates.js";
import { splitFlights, type FlightRow } from "./flights.js";
import { joinToFlights, qualifiesForJoin } from "./joinFlights.js";
import type {
  ClusterGroup,
  DuplicateGroup,
  GroupRow,
  GroupSet,
  RowFlags,
  SameNameGroup,
} from "./types.js";

export interface GroupSetOptions {
  /** Decide duplicates and same-name pairs — only once every row is hashed. */
  withDuplicates?: boolean;
}

export function groupSet(
  rows: readonly GroupRow[],
  { withDuplicates = true }: GroupSetOptions = {},
): GroupSet {
  const duplicates: DuplicateGroup[] = withDuplicates
    ? findDuplicates(rows)
    : [];
  const sameName: SameNameGroup[] = withDuplicates
    ? findSameNameDifferentPayload(rows)
    : [];

  const flags: Record<number, RowFlags> = {};
  const dropped = new Set<number>();
  for (const g of duplicates) {
    for (const d of g.droppedIndexes) {
      dropped.add(d);
      flags[d] = { droppedAsDuplicateOf: g.keptIndex };
    }
  }
  const payloadOf = new Map(
    rows.map((r) => [r.index, r.hashes?.payloadSha256Hex ?? null]),
  );
  for (const g of sameName) {
    for (const i of g.indexes) {
      const mine = payloadOf.get(i);
      const twins = g.indexes.filter((j) => payloadOf.get(j) !== mine);
      flags[i] = { ...flags[i], sameNameDifferentPayload: twins };
    }
  }

  const points: ClusterPoint[] = [];
  let noPosition: number[] = [];
  const flightRowOf = new Map<number, FlightRow>();
  for (const r of rows) {
    if (dropped.has(r.index)) continue;
    const n = r.parsed?.normalized;
    if (n?.lat === undefined || n.lon === undefined) {
      noPosition.push(r.index);
      continue;
    }
    points.push({ index: r.index, lat: n.lat, lon: n.lon, t: n.capturedAtMs });
    flightRowOf.set(r.index, {
      index: r.index,
      t: n.capturedAtMs,
      altRel: n.altRel,
    });
  }
  noPosition.sort((a, b) => a - b);

  // DI-3-S42 — by flight averages within 150 m (was: image by image, 333 m)
  const positioned: ClusterGroup[] = groupByFlightAverages(
    points,
    flightRowOf,
  ).map((c) => ({
    ...c,
    ...splitFlights(
      c.clusterId,
      c.imageIndexes.flatMap((i) => flightRowOf.get(i) ?? []),
    ),
  }));

  // DI-3-S74 — known-camera rows without a position join their flight
  const rowOf = new Map(rows.map((r) => [r.index, r]));
  const join = joinToFlights(
    positioned,
    rows,
    noPosition.filter((i) => {
      const r = rowOf.get(i);
      return r !== undefined && qualifiesForJoin(r);
    }),
  );
  const clusters = join.clusters;
  if (join.joined.length > 0) {
    const joined = new Set(join.joined);
    noPosition = noPosition.filter((i) => !joined.has(i));
  }

  let flights = 0;
  let inFlights = 0;
  let ground = 0;
  let noTime = 0;
  for (const c of clusters) {
    flights += c.flights.length;
    for (const f of c.flights) inFlights += f.imageIndexes.length;
    ground += c.ground.length;
    noTime += c.noTime.length;
  }

  return {
    duplicatesChecked: withDuplicates,
    clusters,
    noPosition,
    unassigned: join.unassigned,
    duplicates,
    sameName,
    flags,
    counts: {
      images: rows.length,
      clusters: clusters.length,
      flights,
      inFlights,
      ground,
      noTime,
      noPosition: noPosition.length,
      joined: join.joined.length,
      duplicatesDropped: dropped.size,
      sameNameGroups: sameName.length,
    },
  };
}
